import { Bullet, Circle, Label, Tooltip, color, p50 } from "@amcharts/amcharts5";
import { AxisRendererY, ColumnSeries, SmoothedXLineSeries, ValueAxis, XYChart, XYCursor } from "@amcharts/amcharts5/xy";

import { LegendData } from "@/classes/BaseChart";
import ChartWithLegend from "@/classes/BaseChart/ChartWithLegend";
import StackedColumnChart from "@/classes/StackedColumnChart";
import { PLAN_MODULE } from "@/constants";
import { normalizeOther } from "@/helpers/dataMapping";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { defaultColorSet } from "@/settings/colors";
import { chartFontSettings } from "@/settings/widgets";
import { ChartDataRecord } from "@/types/charts";

const baseShowLegend = (
  ChartWithLegend.prototype as unknown as {
    showLegend: (inputNames: string[], legendData: [], sortByNames?: boolean, isClickable?: boolean) => void;
  }
).showLegend;

const LINE_COLOR_INDEX = 1;
const STACK_TOTAL_FIELD = "__stackTotal";

export default class StackedColumnLineDecompositionChart extends StackedColumnChart {
  protected createSeries(name: string, index: number, fieldName: string) {
    const paletteLength = this.palette.length;
    const adjusted = (index >= LINE_COLOR_INDEX ? index + 1 : index) % paletteLength;
    return super.createSeries(name, adjusted, fieldName);
  }

  protected chartDataFields(): string[] {
    return [
      this.settings.decompositionMetric,
      this.settings.decompositionMetricSecondary,
      this.settings.displayMetric,
      this.settings.lineMetric,
    ].filter(Boolean) as string[];
  }

  protected mapData(data: []): ChartDataRecord[] {
    const mapped = super.mapData(data);
    const lineMetric = this.settings.lineMetric;
    const firstDimension = this.settings.decompositionMetric;

    const lineByName = new Map<string, number>();
    if (lineMetric) {
      (data as ChartDataRecord[]).forEach((record) => {
        const bucket = normalizeOther(String(record[firstDimension] ?? ""));
        if (!bucket) return;
        lineByName.set(bucket, (lineByName.get(bucket) || 0) + (Number(record[lineMetric]) || 0));
      });
    }

    mapped.forEach((row) => {
      row[STACK_TOTAL_FIELD] = this.metrics.reduce((acc, metric) => acc + (Number(row[metric]) || 0), 0);
      if (lineMetric) row[lineMetric] = lineByName.get(row.name as string) || 0;
    });

    return mapped;
  }

  protected createOverlaySeries(): string[] {
    const lineMetric = this.settings.lineMetric;
    if (!lineMetric) return [];

    ((this.chart as XYChart).series.values.slice(0, this.metrics.length) as ColumnSeries[]).forEach((columnSeries) => {
      columnSeries.set("tooltip", undefined);
      columnSeries.columns.template.remove("tooltipText");
    });

    this.yAxis.set("extraMax", 0.1);

    const lineYAxis = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        min: 0,
        strictMinMax: true,
        extraMax: 0.1,
        renderer: AxisRendererY.new(this.root, { opposite: true }),
        numberFormat: this.getCompactAxesMetricFormatter(lineMetric, this.data),
      })
    );

    const lineRenderer = lineYAxis.get("renderer") as AxisRendererY;
    lineRenderer.grid.template.setAll({ forceHidden: true });
    lineRenderer.labels.template.setAll({ ...chartFontSettings.label });

    const series = (this.chart as XYChart).series.push(
      SmoothedXLineSeries.new(this.root, {
        name: lineMetric,
        xAxis: this.xAxis,
        yAxis: lineYAxis,
        valueYField: lineMetric,
        categoryXField: "name",
        stroke: this.getSeriesColor(LINE_COLOR_INDEX),
        fill: this.getSeriesColor(LINE_COLOR_INDEX),
      })
    );

    series.strokes.template.setAll({ strokeWidth: 2 });

    series.bullets.push(() =>
      Bullet.new(this.root, {
        sprite: Circle.new(this.root, { radius: 4, fill: series.get("fill") }),
      })
    );

    if (this.module === PLAN_MODULE && Boolean(this.settings.showDataLabels)) {
      const lineFormatter = this.formatters?.[lineMetric];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

      series.bullets.push((_root, _series, dataItem) => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerX: p50,
          dy: -30,
          fontSize: labelFontSize,
        });

        label.adapters.add("text", () => {
          const value = dataItem?.get("valueY");
          if (typeof value !== "number") return "";
          return lineFormatter
            ? lineFormatter(value, formatterConfig)
            : this.root.numberFormatter.format(value, formatterConfig);
        });

        label.adapters.add("visible", () => {
          const value = dataItem?.get("valueY");
          return typeof value === "number" ? value !== 0 : false;
        });

        return Bullet.new(this.root, { locationY: 1, sprite: label });
      });
    }

    this.createTooltip(series, (target: Label) => {
      const row = (target?.dataItem?.dataContext ?? {}) as ChartDataRecord;
      return [
        { key: this.settings.decompositionMetric, value: row.name },
        { key: lineMetric, value: row[lineMetric] },
      ];
    });

    series.data.setAll(this.data);

    const stackTooltipCarrier = (this.chart as XYChart).series.push(
      ColumnSeries.new(this.root, {
        xAxis: this.xAxis,
        yAxis: this.yAxis,
        valueYField: STACK_TOTAL_FIELD,
        categoryXField: "name",
        clustered: false,
        fill: color(defaultColorSet[0]),
      })
    );

    stackTooltipCarrier.columns.template.setAll({ fillOpacity: 0, strokeOpacity: 0 });

    const displayMetric = this.settings.displayMetric;
    const metricFormatter = this.formatters?.[displayMetric];
    const formatValue = (value: number): string => {
      const config: Record<string, string | number> = {};
      if (this.settings.compactFormat) config.notation = "compact";
      return metricFormatter ? metricFormatter(value, config) : this.root.numberFormatter.format(value);
    };

    const stackTooltip = stackTooltipCarrier.set("tooltip", Tooltip.new(this.root, {}));
    stackTooltip.label.set("lineHeight", 1.4);
    stackTooltip.label.adapters.add("text", (_text, target) => {
      const row = (target?.dataItem?.dataContext ?? {}) as ChartDataRecord;
      const lines: string[] = [];
      this.metrics.forEach((segment) => {
        const value = Number(row[segment]) || 0;
        if (value === 0) return;
        lines.push(`[bold]${segment}:[/] ${formatValue(value)}`);
      });
      return lines.join("\n");
    });
    stackTooltipCarrier.data.setAll(this.data);

    const cursor = (this.chart as XYChart).set("cursor", XYCursor.new(this.root, { behavior: "none" }));
    cursor.lineY.set("visible", false);

    this.xAxis.get("tooltip")?.set("forceHidden", true);

    return [lineMetric];
  }

  protected showLegend = (): void => {
    const paletteLength = this.palette.length;
    const lineMetric = this.settings.lineMetric;

    const entries = this.metrics.map((metric, index) => ({
      name: metric,
      colorIndex: (index >= LINE_COLOR_INDEX ? index + 1 : index) % paletteLength,
    }));

    if (lineMetric) {
      const lineName =
        this.inputsDictionary?.displayMetrics?.find((item) => item.value === lineMetric)?.title ?? lineMetric;
      entries.push({ name: lineName, colorIndex: LINE_COLOR_INDEX });
    }

    const legendSeries = entries.map((entry) => {
      const proxy = (this.chart as XYChart).series.push(
        ColumnSeries.new(this.root, {
          name: entry.name,
          xAxis: this.xAxis,
          yAxis: this.yAxis,
          clustered: false,
          fill: this.getSeriesColor(entry.colorIndex),
          stroke: this.getSeriesColor(entry.colorIndex),
        })
      );
      proxy.columns.template.set("forceHidden", true);
      return proxy;
    });

    baseShowLegend.call(
      this,
      entries.map((entry) => entry.name),
      legendSeries as LegendData,
      false
    );
  };
}
